import '@atlaskit/css-reset';
import './assets/css/global.css';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { App } from 'components/App';
import { initializeFeatureGatesClientAndReturnUser } from './utils/initializeFeatureGates';
import { UserProvider } from './context/UserContext';
import { getAtlassianUser } from './services/http/user';
import { Router } from './routes/Router';
import { User } from 'typings/types';

import { InitValidator } from './components/InitValidator/InitValidator';
import { OneTrustProvider } from './utils/onetrust-provider';
import { HelmetProvider } from './HelmetProvider';
import { ServicesInitializer } from './components/services-initializer/ServicesInitializer';
import { AppLoadingProvider } from './context/AppLoadingContext';

/*
  IS_STATIC indicates that the page is being rendered statically
  IS_STATIC = true means that the page is NOT SSR; not generated on the server with each request
  but rather during the build process
*/
const isStatic = typeof window !== 'undefined' && window.IS_STATIC;

const createAppRoot = (user?: User | null, userIsInitialized?: boolean, isCSRApp?: boolean) => (
  <OneTrustProvider>
    <UserProvider initialUser={user} initialUserIsInitialized={userIsInitialized}>
      <Router>
        <HelmetProvider>
          <AppLoadingProvider>
            <ServicesInitializer isCSR={isCSRApp}>
              <InitValidator>
                <App />
              </InitValidator>
            </ServicesInitializer>
          </AppLoadingProvider>
        </HelmetProvider>
      </Router>
    </UserProvider>
  </OneTrustProvider>
);

if (isStatic) {
  // CSR
  let root = createRoot(document.getElementById('root') as HTMLElement);
  getAtlassianUser().then((user) => {
    root.render(createAppRoot(user, true, true));
  });
} else {
  // SSR
  initializeFeatureGatesClientAndReturnUser().then((user) => {
    const isUserInit = !!(user && user.email !== '');
    return hydrateRoot(
      document.getElementById('core-root') as HTMLElement,
      createAppRoot(isUserInit ? user : null, isUserInit, false),
    )
  });
}
